import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  useDailyStylistStats,
  useDailyPerformance,
  useRequestTrends,
  useRequestInsights,
  useRequestList,
  useStylingDetails,
  useAiTrendsLatest,
  useAiTrendsHistory,
  useRunAiTrends,
  useRatingAnalysis,
} from '../hooks/useStylingAnalysis.js';
import { stylingAnalysisApi } from '../lib/styling-analysis-api.js';
import { Loader2 } from 'lucide-react';
import type { RatingDateBase, SavedStylingAiAnalysisResponse } from '../types/styling-analysis.js';
import {
  buildPivot,
  buildStylingCsv,
  DailyStylistTable,
} from '../components/styling-analysis/DailyStylistTable.js';
import { RequestTrendsPanel } from '../components/styling-analysis/RequestTrendsPanel.js';
import { RequestListTable } from '../components/styling-analysis/RequestListTable.js';
import { StylingDetailModal } from '../components/styling-analysis/StylingDetailModal.js';
import { AiTrendAnalysisPanel } from '../components/styling-analysis/AiTrendAnalysisPanel.js';
import { AiAnalysisHistoryTable } from '../components/ai-analysis/AiAnalysisHistoryTable.js';
import { StylingPerformanceChart } from '../components/styling-analysis/StylingPerformanceChart.js';
import { RatingAnalysisPanel } from '../components/styling-analysis/RatingAnalysisPanel.js';
import { RequestInsightsPanel } from '../components/styling-analysis/RequestInsightsPanel.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';

/**
 * 完了件数系タブの集計開始日。選定件数の正本である styling_activity_logs の本番稼働日（2026-06-08）に合わせる。
 * それ以前は完了ログが無く、旧ソースの値は最大 24% 過少で比較対象にならないため選べないようにする。
 * API 側の COMPLETION_SERIES_START（styling-analysis routes）と同じ日を指す。
 * @graph-connects none
 */
const COMPLETION_SERIES_START = '2026-06-08';

/**
 * 完了ログを読まないタブの集計開始日。完了ログの稼働日とは無関係なので下限を上げない。
 * API 側の REQUEST_SERIES_START と同じ日を指す。
 * @graph-connects none
 */
const REQUEST_SERIES_START = '2026-04-15';

/**
 * mart__styling_activity_logs（完了ログ）を読まないタブ。
 * trends / requests / ai-analysis / insights は mart__orders のみ、
 * rating は mart__styling_histories（全期間）が母集団。
 * 完了ログの稼働日（2026-06-08）で日付を切ると正当な履歴が見えなくなるので下限を上げない。
 * @graph-connects none
 */
const REQUEST_ONLY_TABS: ReadonlySet<TabKey> = new Set<TabKey>([
  'trends',
  'requests',
  'ai-analysis',
  'rating',
  'insights',
]);

/** @graph-connects none */
const PAGE_SIZE = 50;

/** @graph-connects none */
type TabKey =
  | 'performance'
  | 'stylist'
  | 'trends'
  | 'rating'
  | 'insights'
  | 'ai-analysis'
  | 'requests';

/** @graph-connects none */
const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'performance', labelKey: 'styling.tabPerformance' },
  { key: 'stylist', labelKey: 'styling.tabStylist' },
  { key: 'trends', labelKey: 'styling.tabTrends' },
  { key: 'rating', labelKey: 'styling.tabRating' },
  { key: 'insights', labelKey: 'styling.tabInsights' },
  { key: 'ai-analysis', labelKey: 'styling.tabAiAnalysis' },
  { key: 'requests', labelKey: 'styling.tabRequests' },
];

/** @graph-connects none */
interface DrilldownTarget {
  date: string;
  stylistId: string;
  type: 'completed' | 'rejected' | 'error';
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリング分析ページ。7タブ構成: 選定パフォーマンス推移チャート、スタイリスト別実績ピボット、リクエスト傾向、レーティング、リクエスト深掘り（セグメント分析）、AI分析、リクエスト一覧。
 * @graph-connects useDailyPerformance [calls] Fetch daily performance stats for chart
 * @graph-connects useDailyStylistStats [calls] Fetch daily per-stylist stats
 * @graph-connects useStylingDetails [calls] Fetch styling detail for drilldown
 * @graph-connects useRequestTrends [calls] Fetch request trends
 * @graph-connects useRequestInsights [calls] Fetch deep request insights (segment, length correlation, cross-tabs)
 * @graph-connects useAiTrendsLatest [calls] Fetch latest AI trend analysis
 * @graph-connects useAiTrendsHistory [calls] Fetch AI trend analysis history
 * @graph-connects useRunAiTrends [calls] Run AI trend analysis
 * @graph-connects useRequestList [calls] Fetch raw request list
 * @graph-connects DailyStylistTable [calls] Render pivot table
 * @graph-connects StylingDetailModal [calls] Render drilldown modal
 * @graph-connects RequestTrendsPanel [calls] Render request trends
 * @graph-connects RequestInsightsPanel [calls] Render deep request insights with So What annotations
 * @graph-connects AiTrendAnalysisPanel [calls] Render AI analysis results
 * @graph-connects RequestListTable [calls] Render raw request list
 * @graph-connects StylingPerformanceChart [calls] Render performance chart
 * @graph-connects RatingAnalysisPanel [calls] Render rating analysis
 * @graph-connects useRatingAnalysis [calls] Fetch rating analysis data
 * @graph-connects buildPivot [calls] Build pivot data for CSV export
 * @graph-connects buildStylingCsv [calls] Build CSV string for export
 */
export function StylingAnalysisPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('performance');
  const [from, setFrom] = useState(COMPLETION_SERIES_START);
  const [to, setTo] = useState('');
  const [requestOffset, setRequestOffset] = useState(0);
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const [ratingDateBase, setRatingDateBase] = useState<RatingDateBase>('completed');

  const performanceQuery = useDailyPerformance(
    from || undefined,
    to || undefined,
    activeTab === 'performance',
  );
  const stylistQuery = useDailyStylistStats(
    activeTab === 'stylist' ? from || undefined : undefined,
    activeTab === 'stylist' ? to || undefined : undefined,
  );
  const trendsQuery = useRequestTrends(
    activeTab === 'trends' ? from || undefined : undefined,
    activeTab === 'trends' ? to || undefined : undefined,
  );
  const ratingQuery = useRatingAnalysis(
    activeTab === 'rating' ? from || undefined : undefined,
    activeTab === 'rating' ? to || undefined : undefined,
    activeTab === 'rating' ? ratingDateBase : undefined,
    activeTab === 'rating',
  );
  const insightsQuery = useRequestInsights(
    activeTab === 'insights' ? from || undefined : undefined,
    activeTab === 'insights' ? to || undefined : undefined,
    activeTab === 'insights',
  );
  const [aiSelectedData, setAiSelectedData] = useState<SavedStylingAiAnalysisResponse | null>(null);
  const [aiViewMode, setAiViewMode] = useState<'latest' | 'history'>('latest');
  const aiLatestQuery = useAiTrendsLatest(activeTab === 'ai-analysis');
  const aiHistoryQuery = useAiTrendsHistory(
    activeTab === 'ai-analysis' && aiViewMode === 'history',
  );
  const aiRunMutation = useRunAiTrends();
  const aiData = aiSelectedData ?? aiLatestQuery.data ?? null;
  const requestsQuery = useRequestList(
    activeTab === 'requests' ? from || undefined : undefined,
    activeTab === 'requests' ? to || undefined : undefined,
    activeTab === 'requests' ? PAGE_SIZE : undefined,
    activeTab === 'requests' ? requestOffset : undefined,
  );

  const detailQuery = useStylingDetails(
    drilldown?.date ?? null,
    drilldown?.type ?? null,
    drilldown?.stylistId ?? undefined,
  );

  const activeQuery =
    activeTab === 'performance'
      ? performanceQuery
      : activeTab === 'stylist'
        ? stylistQuery
        : activeTab === 'trends'
          ? trendsQuery
          : activeTab === 'rating'
            ? ratingQuery
            : activeTab === 'insights'
              ? insightsQuery
              : requestsQuery;

  const handleCellClick = useCallback(
    (date: string, stylistId: string, type: 'completed' | 'rejected' | 'error') => {
      setDrilldown({ date, stylistId, type });
    },
    [],
  );

  const handleExportCsv = useCallback(() => {
    if (!stylistQuery.data) return;
    const { stylists, rows } = buildPivot(
      stylistQuery.data.records,
      stylistQuery.data.rejections,
      stylistQuery.data.errors,
    );
    const csv = buildStylingCsv(stylists, rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const fromDate = stylistQuery.data.period.from;
    const toDate = stylistQuery.data.period.to;
    anchor.download = `styling_analysis_${fromDate}_${toDate}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [stylistQuery.data]);

  const drilldownStylistName = useMemo(() => {
    if (!drilldown?.stylistId || !stylistQuery.data) return null;
    const rec =
      stylistQuery.data.records.find((r) => r.stylistId === drilldown.stylistId) ??
      stylistQuery.data.rejections.find((r) => r.stylistId === drilldown.stylistId) ??
      stylistQuery.data.errors.find((r) => r.stylistId === drilldown.stylistId);
    return rec?.stylistName ?? null;
  }, [drilldown, stylistQuery.data]);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Styling
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('styling.title')}</h1>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('styling.description')}</p>

      <div className="mt-6 flex flex-wrap items-end gap-4">
        <DateRangeFilter
          from={from}
          to={to}
          minDate={
            REQUEST_ONLY_TABS.has(activeTab) ? REQUEST_SERIES_START : COMPLETION_SERIES_START
          }
          onFromChange={(v) => {
            setFrom(v);
            setRequestOffset(0);
          }}
          onToChange={(v) => {
            setTo(v);
            setRequestOffset(0);
          }}
        />
        {activeQuery.data?.period && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {activeQuery.data.period.from} 〜 {activeQuery.data.period.to}
          </span>
        )}
        {activeTab === 'stylist' && stylistQuery.data && (
          <button
            type="button"
            onClick={handleExportCsv}
            className="ml-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-4 py-1.5 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            {t('common.csvExport')}
          </button>
        )}
      </div>

      <div className="mt-6 flex gap-1 border-b border-[var(--color-border)]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setActiveTab(tab.key);
              setRequestOffset(0);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-b-2 border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <Card className="mt-4 overflow-hidden p-0">
        {activeQuery.isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="loading-spinner" />
            <span className="ml-3 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</span>
          </div>
        )}
        {activeQuery.error && (
          <div className="px-6 py-12 text-center text-sm text-[var(--color-error)]">
            {t('common.fetchError')}
          </div>
        )}

        {activeTab === 'performance' && performanceQuery.data && (
          <StylingPerformanceChart records={performanceQuery.data.dailyStats} />
        )}

        {activeTab === 'stylist' && stylistQuery.data && (
          <DailyStylistTable
            records={stylistQuery.data.records}
            rejections={stylistQuery.data.rejections}
            errors={stylistQuery.data.errors}
            onCellClick={handleCellClick}
          />
        )}

        {activeTab === 'trends' && trendsQuery.data && (
          <div className="p-6">
            <RequestTrendsPanel
              sceneTrends={trendsQuery.data.sceneTrends}
              monthlyTrends={trendsQuery.data.monthlyTrends}
            />
          </div>
        )}

        {activeTab === 'rating' && ratingQuery.data && (
          <RatingAnalysisPanel
            daily={ratingQuery.data.daily}
            dateBase={ratingDateBase}
            onDateBaseChange={setRatingDateBase}
          />
        )}

        {activeTab === 'insights' && insightsQuery.data && (
          <div className="p-6">
            <RequestInsightsPanel
              requestRateBySegment={insightsQuery.data.requestRateBySegment}
              requestLengthVsRestyling={insightsQuery.data.requestLengthVsRestyling}
              monthlySegmentTrends={insightsQuery.data.monthlySegmentTrends}
              sceneBySegment={insightsQuery.data.sceneBySegment}
            />
          </div>
        )}

        {activeTab === 'ai-analysis' && (
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setAiSelectedData(null);
                  setAiViewMode('latest');
                  aiRunMutation.mutate(
                    { from: from || undefined, to: to || undefined },
                    {
                      onSuccess: (result) => {
                        setAiSelectedData(result);
                      },
                    },
                  );
                }}
                disabled={aiRunMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {aiRunMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="text-xs">▶</span>
                )}
                {t('styling.runAnalysis')}
              </button>
              <div className="flex rounded-lg border">
                <button
                  type="button"
                  onClick={() => {
                    setAiViewMode('latest');
                  }}
                  className={`px-3 py-1.5 text-xs font-medium ${aiViewMode === 'latest' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'}`}
                >
                  {t('styling.latestResult')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAiViewMode('history');
                  }}
                  className={`px-3 py-1.5 text-xs font-medium ${aiViewMode === 'history' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'}`}
                >
                  {t('styling.historyView')}
                </button>
              </div>
            </div>

            {aiRunMutation.isPending ? (
              <div className="flex flex-col items-center py-16 text-gray-400">
                <Loader2 className="mb-3 h-8 w-8 animate-spin" />
                <p className="text-sm">{t('styling.aiAnalyzing')}</p>
                <p className="text-xs text-gray-300">{t('styling.aiAnalyzeHint')}</p>
              </div>
            ) : aiViewMode === 'history' ? (
              <div>
                {aiHistoryQuery.isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <AiAnalysisHistoryTable
                    items={aiHistoryQuery.data?.items ?? []}
                    onSelect={(item) => {
                      void stylingAnalysisApi.getAiTrendsDetail(item.id).then((d) => {
                        if (d) {
                          setAiSelectedData(d);
                          setAiViewMode('latest');
                        }
                      });
                    }}
                    getSummary={(item) => item.summary}
                  />
                )}
              </div>
            ) : aiData ? (
              <AiTrendAnalysisPanel analysis={aiData.analysis} requestCount={aiData.requestCount} />
            ) : (
              <div className="py-12 text-center">
                <p className="text-sm text-gray-500">{t('styling.noAiAnalysis')}</p>
                <p className="mt-1 text-xs text-gray-400">{t('styling.aiAnalyzePrompt')}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && requestsQuery.data && (
          <div className="p-6">
            <RequestListTable
              items={requestsQuery.data.items}
              totalCount={requestsQuery.data.totalCount}
              limit={PAGE_SIZE}
              offset={requestOffset}
              onPageChange={setRequestOffset}
            />
          </div>
        )}
      </Card>

      {drilldown && (
        <StylingDetailModal
          date={drilldown.date}
          type={drilldown.type}
          stylistName={drilldownStylistName}
          details={detailQuery.data?.details ?? []}
          isLoading={detailQuery.isLoading}
          onClose={() => {
            setDrilldown(null);
          }}
        />
      )}
    </div>
  );
}
